import {ArrowDownIcon, ArrowUpIcon} from "@heroicons/react/24/outline";
import Checkbox from "../../../../general/Checkbox.tsx";
import {cn} from "../../../../../lib/utils.ts";
import {BookingSortColumn, type BookingSort} from "../types.ts";

interface BookingTableHeaderProps {
    allSelected: boolean;
    someSelected: boolean;
    selectAllDisabled: boolean;
    onToggleSelectAll: () => void;
    sort: BookingSort;
    onSortChange: (column: BookingSortColumn) => void;
}

export function SortableHeader<Column extends string>({
    label,
    column,
    sort,
    onSortChange,
    alignRight = false,
    className,
}: {
    label: string;
    column: Column;
    sort: {column: Column; ascending: boolean};
    onSortChange: (column: Column) => void;
    alignRight?: boolean;
    className?: string;
}) {
    const isActive = sort.column === column;
    const Icon = sort.ascending ? ArrowUpIcon : ArrowDownIcon;
    return (
        <button
            type="button"
            onClick={() => onSortChange(column)}
            className={cn(
                "inline-flex items-center gap-1 text-xs font-medium",
                isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
                alignRight && "justify-end",
                className,
            )}
        >
            {label}
            {isActive && <Icon className="size-3" aria-hidden />}
        </button>
    );
}

export function BookingTableHeader({
    allSelected,
    someSelected,
    selectAllDisabled,
    onToggleSelectAll,
    sort,
    onSortChange,
}: BookingTableHeaderProps) {
    return (
        <thead className="sticky top-0 z-10 bg-white shadow-[inset_0_-1px_0_0_#e5e7eb]">
            <tr>
                <th className="w-14 py-2 pl-4 pr-2">
                    <span className="flex justify-center">
                        <Checkbox
                            value={allSelected}
                            indeterminate={someSelected && !allSelected}
                            disabled={selectAllDisabled}
                            onUpdateFunc={onToggleSelectAll}
                            ariaLabel="Selecteer alle boekingen"
                        />
                    </span>
                </th>
                <th className="w-8 px-1 py-2" />
                <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground">Type</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-muted-foreground">Details</th>
                <th className="px-2 py-2 text-left">
                    <SortableHeader label="Datum" column={BookingSortColumn.Date} sort={sort} onSortChange={onSortChange} />
                </th>
                <th className="px-2 py-2 text-right">
                    <SortableHeader label="Debet" column={BookingSortColumn.Debit} sort={sort} onSortChange={onSortChange} alignRight />
                </th>
                <th className="px-2 py-2 text-right">
                    <SortableHeader label="Credit" column={BookingSortColumn.Credit} sort={sort} onSortChange={onSortChange} alignRight />
                </th>
                <th className="py-2 pl-2 pr-4 text-left text-xs font-medium text-muted-foreground">Btw</th>
            </tr>
        </thead>
    );
}
